/**
 * Payload Breakdown Component - Shows complete 13-byte BLE packet structure
 */

import React, { useState } from 'react';
import { useDiaperStore } from '../../stores/diaperStore';
import { useSimulatorStore } from '../../stores/simulatorStore';
import { buildSensorPacket } from '../../lib/utils';

export const PayloadBreakdown: React.FC = () => {
  const [showBreakdown, setShowBreakdown] = useState(true);
  const { diaperModels } = useDiaperStore();
  const {
    selectedClip,
    wetnessLevel,
    statusCode,
    battery,
    diaperCounter,
    adcValues,
    setAdcValues,
  } = useSimulatorStore();

  if (!selectedClip) return null;

  const diaperModelCode = selectedClip.diaperModelCode || diaperModels[0]?.code || 30;
  const diaperModel = diaperModels.find((m) => m.code === diaperModelCode) || null;

  const payload = buildSensorPacket({
    wetness: wetnessLevel,
    statusCode,
    battery,
    diaperModel: selectedClip.diaperModelCode || 30,
    diaperCounter,
    adcValues,
    firmwareRevision: selectedClip.firmwareRevision,
  });

  const handleAdcChange = (index: number, value: string) => {
    const numValue = parseInt(value) || 0;
    const clampedValue = Math.max(0, Math.min(255, numValue));
    const newAdcValues = [...adcValues];
    newAdcValues[index] = clampedValue;
    setAdcValues(newAdcValues);
  };

  // Get leak thresholds from diaper model
  const leakThresholds = diaperModel?.leakThreshold?.adcValues || [];
  
  const adcFields = [
    { byte: 7, label: 'ADC C5 (Ref–L16)', leakIndex: 4 },
    { byte: 8, label: 'ADC C4 (L16–L8)', leakIndex: 3 },
    { byte: 9, label: 'ADC C3 (L8–L4)', leakIndex: 2 },
    { byte: 10, label: 'ADC C2 (L4–L2)', leakIndex: 1 },
    { byte: 11, label: 'ADC C1 (L2–L1)', leakIndex: 0 },
  ];

  return (
    <div className="mt-6">
      <label className="flex items-center gap-2 cursor-pointer mb-4">
        <input
          type="checkbox"
          checked={showBreakdown}
          onChange={(e) => setShowBreakdown(e.target.checked)}
          className="w-4 h-4 cursor-pointer"
        />
        <span className="font-semibold">
          Show Complete 13-byte Payload Breakdown (edit ADC values in table)
        </span>
      </label>

      {showBreakdown && (
        <div className="overflow-x-auto border border-gray-300 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#1D407B] text-white">
                <th className="px-4 py-3 text-left font-semibold">Byte</th>
                <th className="px-4 py-3 text-left font-semibold">Value</th>
                <th className="px-4 py-3 text-left font-semibold">Hex</th>
                <th className="px-4 py-3 text-left font-semibold">Field Name</th>
              </tr>
            </thead>
            <tbody>
              {/* Byte 0 - Firmware Version */}
              <tr className="border-b border-gray-200 bg-white hover:bg-gray-50">
                <td className="px-4 py-3">[0]</td>
                <td className="px-4 py-3 font-mono">{payload[0]}</td>
                <td className="px-4 py-3 font-mono">0x{payload[0].toString(16).toUpperCase().padStart(2, '0')}</td>
                <td className="px-4 py-3">Firmware Version</td>
              </tr>

              {/* Byte 1 - Status Code */}
              <tr className="border-b border-gray-200 bg-gray-50 hover:bg-gray-100">
                <td className="px-4 py-3">[1]</td>
                <td className="px-4 py-3 font-mono">{payload[1]}</td>
                <td className="px-4 py-3 font-mono">0x{payload[1].toString(16).toUpperCase().padStart(2, '0')}</td>
                <td className="px-4 py-3">Status Code</td>
              </tr>

              {/* Byte 2 - Wetness LSB */}
              <tr className="border-b border-gray-200 bg-white hover:bg-gray-50">
                <td className="px-4 py-3">[2]</td>
                <td className="px-4 py-3 font-mono">{payload[2]}</td>
                <td className="px-4 py-3 font-mono">0x{payload[2].toString(16).toUpperCase().padStart(2, '0')}</td>
                <td className="px-4 py-3">Wetness LSB (0)</td>
              </tr>

              {/* Byte 3 - Wetness MSB */}
              <tr className="border-b border-gray-200 bg-gray-50 hover:bg-gray-100">
                <td className="px-4 py-3">[3]</td>
                <td className="px-4 py-3 font-mono">{payload[3]}</td>
                <td className="px-4 py-3 font-mono">0x{payload[3].toString(16).toUpperCase().padStart(2, '0')}</td>
                <td className="px-4 py-3">Wetness MSB</td>
              </tr>

              {/* Byte 4 - Model LSB */}
              <tr className="border-b border-gray-200 bg-white hover:bg-gray-50">
                <td className="px-4 py-3">[4]</td>
                <td className="px-4 py-3 font-mono">{payload[4]}</td>
                <td className="px-4 py-3 font-mono">0x{payload[4].toString(16).toUpperCase().padStart(2, '0')}</td>
                <td className="px-4 py-3">Model LSB ({payload[4]})</td>
              </tr>

              {/* Byte 5 - Model MSB */}
              <tr className="border-b border-gray-200 bg-gray-50 hover:bg-gray-100">
                <td className="px-4 py-3">[5]</td>
                <td className="px-4 py-3 font-mono">{payload[5]}</td>
                <td className="px-4 py-3 font-mono">0x{payload[5].toString(16).toUpperCase().padStart(2, '0')}</td>
                <td className="px-4 py-3">Model MSB</td>
              </tr>

              {/* Byte 6 - Diaper Counter */}
              <tr className="border-b border-gray-200 bg-white hover:bg-gray-50">
                <td className="px-4 py-3">[6]</td>
                <td className="px-4 py-3 font-mono">{payload[6]}</td>
                <td className="px-4 py-3 font-mono">0x{payload[6].toString(16).toUpperCase().padStart(2, '0')}</td>
                <td className="px-4 py-3">Diaper Counter</td>
              </tr>

              {/* Bytes 7-11 - ADC Values (editable) */}
              {adcFields.map(({ byte, label, leakIndex }) => {
                const value = payload[byte];
                const leakThreshold = leakThresholds[leakIndex];
                const hasLeakThreshold = leakThreshold !== undefined;

                return (
                  <tr key={byte} className="border-b border-gray-200 bg-gray-50 hover:bg-gray-100">
                    <td className="px-4 py-3">[{byte}]</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="255"
                          value={adcValues[byte - 7]}
                          onChange={(e) => handleAdcChange(byte - 7, e.target.value)}
                          className="w-16 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        {hasLeakThreshold && (
                          <span className="flex items-center gap-1 text-red-600 text-xs">
                            <span className="inline-block w-2 h-2 bg-red-600 rounded-full"></span>
                            Leak at: {leakThreshold}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono">0x{value.toString(16).toUpperCase().padStart(2, '0')}</td>
                    <td className="px-4 py-3">{label}</td>
                  </tr>
                );
              })}

              {/* Byte 12 - Battery */}
              <tr className="border-b border-gray-200 bg-white hover:bg-gray-50">
                <td className="px-4 py-3">[12]</td>
                <td className="px-4 py-3 font-mono">{payload[12]}</td>
                <td className="px-4 py-3 font-mono">0x{payload[12].toString(16).toUpperCase().padStart(2, '0')}</td>
                <td className="px-4 py-3">Battery %</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

